import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext } from "@/lib/teacher-context";
import { supabase } from "@/integrations/supabase/client";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AICallout } from "@/components/makai/primitives";

export const Route = createFileRoute("/teacher/gradebook")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({ meta: [{ title: "Gradebook — MAKAI" }] }),
  component: GradebookPage,
});

function GradebookPage() {
  const ctx = useTeacherContext();
  const [streamId, setStreamId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [data, setData] = useState<{ learners: any[]; assessments: any[]; marks: Map<string, any> }>({ learners: [], assessments: [], marks: new Map() });

  useEffect(() => { if (!streamId && ctx.streams.length) setStreamId(ctx.streams[0].id); }, [ctx.streams, streamId]);

  const subjectsForStream = useMemo(
    () => ctx.assignments.filter((a) => a.stream.id === streamId && a.subject).map((a) => a.subject!),
    [ctx.assignments, streamId]
  );
  useEffect(() => { if (subjectsForStream.length && !subjectsForStream.find((s) => s.id === subjectId)) setSubjectId(subjectsForStream[0].id); }, [subjectsForStream, subjectId]);

  useEffect(() => {
    if (!streamId || !subjectId) return;
    (async () => {
      const [{ data: learners }, { data: asmts }] = await Promise.all([
        supabase.from("learners").select("id, first_name, last_name").eq("current_stream_id", streamId).eq("status","active").order("first_name"),
        supabase.from("assessments").select("id, title, max_score, weight, kind").eq("stream_id", streamId).eq("subject_id", subjectId).order("assessed_on", { ascending: true, nullsFirst: false }),
      ]);
      const ids = (asmts ?? []).map((a: any) => a.id);
      const marksMap = new Map<string, any>();
      if (ids.length) {
        const { data: marks } = await supabase.from("assessment_marks").select("assessment_id, learner_id, score").in("assessment_id", ids);
        for (const m of marks ?? []) marksMap.set(`${m.assessment_id}|${m.learner_id}`, m);
      }
      setData({ learners: learners ?? [], assessments: asmts ?? [], marks: marksMap });
    })();
  }, [streamId, subjectId]);

  const totals = useMemo(() => {
    const totalWeight = data.assessments.reduce((s, a) => s + Number(a.weight || 1), 0) || 1;
    return data.learners.map((l) => {
      let weighted = 0; let captured = 0;
      for (const a of data.assessments) {
        const m = data.marks.get(`${a.id}|${l.id}`);
        if (m?.score != null) {
          weighted += (Number(m.score) / Number(a.max_score || 100)) * 100 * Number(a.weight || 1);
          captured += Number(a.weight || 1);
        }
      }
      const pct = captured ? weighted / totalWeight : null;
      return { learner: l, pct, captured: captured > 0 };
    });
  }, [data]);

  function gradeOf(pct: number | null) {
    if (pct == null) return { g: "—", cls: "text-muted-foreground" };
    if (pct >= 80) return { g: "A", cls: "text-primary" };
    if (pct >= 70) return { g: "B", cls: "text-sky" };
    if (pct >= 60) return { g: "C", cls: "text-gold-foreground" };
    if (pct >= 50) return { g: "D", cls: "text-orange-600" };
    return { g: "F", cls: "text-destructive" };
  }

  function exportCsv() {
    const head = ["Learner", ...data.assessments.map((a) => a.title), "Overall %"];
    const lines = [head];
    for (const row of totals) {
      const name = `${row.learner.first_name} ${row.learner.last_name}`;
      const cells = data.assessments.map((a) => data.marks.get(`${a.id}|${row.learner.id}`)?.score ?? "");
      lines.push([name, ...cells, row.pct != null ? row.pct.toFixed(1) : ""]);
    }
    const csv = lines.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "gradebook.csv"; a.click(); URL.revokeObjectURL(url);
  }

  return (
    <AppShell role="teacher" title="Gradebook" subtitle="Aggregate marks across assessments by class and subject."
      action={<Button variant="outline" onClick={exportCsv}><Download className="mr-1 h-4 w-4" /> Export</Button>}>
      <div className="grid gap-3 rounded-2xl border border-border bg-card p-4 shadow-soft sm:grid-cols-2">
        <select value={streamId} onChange={(e) => setStreamId(e.target.value)} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
          {ctx.streams.map((s) => <option key={s.id} value={s.id}>{s.class_name} · {s.name}</option>)}
        </select>
        <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
          {subjectsForStream.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      <div className="mt-4 overflow-auto rounded-2xl border border-border bg-card shadow-soft">
        <table className="min-w-full text-sm">
          <thead className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="sticky left-0 z-10 bg-muted/60 px-3 py-2">Learner</th>
              {data.assessments.map((a) => (
                <th key={a.id} className="px-3 py-2 text-center">
                  <div>{a.title}</div>
                  <div className="text-[10px] font-normal text-muted-foreground">/{a.max_score} · w{a.weight}</div>
                </th>
              ))}
              <th className="px-3 py-2 text-center">Overall</th>
              <th className="px-3 py-2 text-center">Grade</th>
            </tr>
          </thead>
          <tbody>
            {totals.map(({ learner, pct }) => {
              const g = gradeOf(pct);
              return (
                <tr key={learner.id} className="border-t border-border">
                  <td className="sticky left-0 bg-card px-3 py-2 font-medium">{learner.first_name} {learner.last_name}</td>
                  {data.assessments.map((a) => {
                    const m = data.marks.get(`${a.id}|${learner.id}`);
                    return <td key={a.id} className="px-3 py-2 text-center">{m?.score ?? <span className="text-muted-foreground">—</span>}</td>;
                  })}
                  <td className="px-3 py-2 text-center font-semibold">{pct != null ? `${pct.toFixed(0)}%` : "—"}</td>
                  <td className={`px-3 py-2 text-center font-display font-bold ${g.cls}`}>{g.g}</td>
                </tr>
              );
            })}
            {!totals.length && (
              <tr><td colSpan={3} className="px-3 py-8 text-center text-sm text-muted-foreground">No data yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-6">
        <AICallout
          description="Write comments, flag drops, draft parent messages — grounded in this gradebook."
          suggestions={[
            "Write report comments for my class",
            "Flag students who dropped 2+ grades",
            "Draft a parent message about results",
            "Suggest interventions for low scorers",
          ]}
        />
      </div>
    </AppShell>
  );
}
